import * as ko from "knockout";
import type { components, Observable, ObservableArray } from "knockout";

import * as Backend from "../../../backend/v1/sdk.gen";
import type { LocationPickerTankPosition } from "../../../backend/v1/types.gen";
import { handleBackendError } from "../../../backend/v1/utils";
import { assert } from "../../../lib/assert";
import { translated } from "../../../lib/localize";
import type { LocationIdString, LocationItem } from "./locationPicker";

export type TankId = number;

export interface TankPosition extends LocationPickerTankPosition {
    selected: Observable<boolean>;
}

/**
 * Tank picker.
 *
 * @param location - ko.observable or object (dict) that holds the selected location
 * item which will be used to query all tank belonging to that location. In case if
 * object (dict) it has to have a type attribute of ('building', 'area', 'room', 'rack')
 * and corresponding [area/room/etc]_id If used with location-picker, this should be
 * the same variable as given to location-picker as params.selectedLocation
 *
 * @param showTankTrigger - ko.observable used to scroll selected tank into
 * view. To trigger, call showTankTrigger.valueHasMutated() or set any true-ish value
 * to it.
 *
 * @param unselectTankTrigger - ko.observable used to unselect a maybe
 * currently selected tank item. To trigger, call unselectTankTrigger.valueHasMutated()
 *
 * @param rememberSelectionTrigger - ko.observable used to store the currently
 * selected tank so it can be restored later. To trigger, call
 * rememberSelectionTrigger.valueHasMutated()
 *
 * @param restoreSelectionTrigger - ko.observable to reset the currently
 * selected tank to the previously remembered one, that was store using restoreSelectionTrigger.
 * To trigger, call restoreSelectionTrigger.valueHasMutated()
 *
 * @param parentLocation - full id string of a location (e.g. 1-2-3) picker
 * shows only tanks of this location or its children; on selecting a parent of the
 * parentLocation, it shows only tanks of parentLocation itself; tanks of siblings of
 * parentLocation are not included
 *
 * @param preselectTank - id of tank to pre-select
 *
 * @param selectedPosition - ko.observable to hold the selected tank
 *
 * @param captionText - Text to show if no tank could be found or is selected.
 * Default: "No tanks found.".
 *
 */
interface TankPickerParams {
    location: LocationItem;
    showTankTrigger?: Observable;
    unselectTankTrigger?: Observable;
    rememberSelectionTrigger?: Observable;
    restoreSelectionTrigger?: Observable;
    parentLocation?: LocationIdString;
    preselectTank?: TankId;
    selectedPosition?: Observable<TankPosition>;
    captionText?: string;
}

class TankPickerViewModel {
    public defaultCaption: string;
    public componentElement: HTMLElement;
    public isLoading: Observable<boolean>;
    public captionText: Observable<string>;
    public rememberedTank: Observable<TankPosition>;
    public lastSelectedLocation: LocationIdString;
    public selectedLocation: Observable<LocationItem>;
    public positionList: ObservableArray<TankPosition>;
    public selectedPosition: Observable<TankPosition>;
    public preselectTank: TankId | string;
    public parentLocation: LocationIdString;

    constructor(params: TankPickerParams, componentElement: HTMLElement) {
        assert(ko.isObservable(params.selectedPosition), "params.selectedPosition is not a ko.observable");

        this.defaultCaption = params.captionText || translated("No tank to show.");
        this.componentElement = componentElement;
        this.selectedPosition = params.selectedPosition;
        this.parentLocation = params.parentLocation;
        this.preselectTank = params.preselectTank;
        this.isLoading = ko.observable(false);
        this.captionText = ko.observable(this.defaultCaption);
        this.rememberedTank = ko.observable();
        this.selectedLocation = ko.isObservable(params.location) ? params.location : ko.observable(params.location);
        this.positionList = ko.observableArray();
        this.selectedLocation.subscribe((item) => {
            if (!item) {
                return;
            }
            if (this.lastSelectedLocation !== item.id) {
                this.lastSelectedLocation = item.id;
                this.getPositions(item);
            }
        });

        if (ko.isObservable(params.showTankTrigger)) {
            // trigger with params.showTankTrigger.valueHasMutated()
            params.showTankTrigger.extend({ notify: "always" });
            params.showTankTrigger.subscribe(() => {
                this.showPosition(params.selectedPosition());
            });
        }

        if (ko.isObservable(params.unselectTankTrigger)) {
            // trigger with params.unselectTankTrigger.valueHasMutated()
            params.unselectTankTrigger.extend({ notify: "always" });
            params.unselectTankTrigger.subscribe(() => {
                this.unselectItem();
            });
        }

        if (ko.isObservable(params.rememberSelectionTrigger)) {
            // trigger with params.rememberSelectionTrigger.valueHasMutated()
            params.rememberSelectionTrigger.extend({ notify: "always" });
            params.rememberSelectionTrigger.subscribe(() => {
                this.rememberedTank(params.selectedPosition());
            });
        }

        if (ko.isObservable(params.restoreSelectionTrigger)) {
            // trigger with params.restoreSelectionTrigger.valueHasMutated()
            params.restoreSelectionTrigger.extend({ notify: "always" });
            params.restoreSelectionTrigger.subscribe(() => {
                const rTank = this.rememberedTank();
                const sTank = params.selectedPosition();

                if (rTank && (!sTank || rTank.location_reference !== sTank.location_reference)) {
                    this.selectItem(rTank);
                } else if (!rTank && sTank) {
                    this.unselectItem();
                }
            });
        }

        // initially fetch positions
        if (this.selectedLocation()) {
            this.getPositions(this.selectedLocation());
        }
    }

    public clickItem = (item: TankPosition) => {
        if (item.location_reference) {
            this.selectItem(item);
            this.showPosition(item);
        }
    };

    // fetch location tree
    private getPositions = (locationItem: LocationItem) => {
        if (this.isLoading()) {
            return;
        }

        this.isLoading(true);
        this.positionList([]);

        let locationId: string;
        if (
            this.parentLocation === undefined ||
            locationItem.id.split("-").length >= this.parentLocation.split("-").length
        ) {
            locationId = locationItem.id;
        } else {
            locationId = this.parentLocation;
        }

        Backend.getPickerTankList({
            path: {
                location_id: locationId,
            },
        })
            .then(({ data: tankList }) => {
                this.captionText("");
                tankList.forEach((item: TankPosition) => {
                    item.selected = ko.observable();
                    if (
                        this.selectedPosition() &&
                        item.location_reference === this.selectedPosition().location_reference
                    ) {
                        this.selectItem(item);
                    } else if (!this.selectedPosition() && item.location_reference === this.preselectTank) {
                        this.selectItem(item);
                    }
                });
                this.positionList(tankList as TankPosition[]);
                if (this.selectedPosition()) {
                    this.showPosition(this.selectedPosition());
                }
            })
            .catch(
                handleBackendError({
                    callback: this.captionText,
                }),
            )
            .finally(() => {
                this.isLoading(false);
            });
    };

    private showPosition = (item: TankPosition) => {
        if (!item) {
            this.componentElement.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        } else {
            this.componentElement
                .querySelector(`[location_reference="${item.location_reference}"]`)
                ?.scrollIntoView({ behavior: "smooth" });
        }
    };

    private unselectItem = (doNotSetUndefined = false) => {
        if (this.selectedPosition()) {
            this.selectedPosition().selected(false);
        }
        if (!doNotSetUndefined) {
            this.selectedPosition(undefined);
        }
    };

    private selectItem = (item: TankPosition) => {
        this.unselectItem(true);
        item.selected(true);
        this.selectedPosition(item);
    };
}

export class TankPickerComponent {
    constructor() {
        // biome-ignore lint/correctness/noConstructorReturn:
        return {
            viewModel: {
                createViewModel: (params: TankPickerParams, componentInfo: components.ComponentInfo) => {
                    return new TankPickerViewModel(params, componentInfo.element as HTMLElement);
                },
            },
            template: `
                <div class="tank-picker">
                    <!-- ko foreach: positionList -->
                    <div class="tank"
                         data-bind="css: {selectable: location_reference,
                                          selected: selected,
                                          in_use: in_use},
                                    attr: {location_reference: location_reference},
                                    click: $parent.clickItem">
                        <span class="tank_position" data-bind="text: tank_position || translated('Free slots')"></span>
                    </div>
                    <!-- /ko -->
                    <div class="loading_circle" data-bind="visible: isLoading"></div>
                    <div class="caption" data-bind="hidden: isLoading() || positionList().length,
                                                    text: captionText"></div>
                </div>
            `,
        };
    }
}
